import type { UaParsed } from '@fraudeai/schema';

/**
 * Разбор User-Agent.
 *
 * Правилам нужна не красивая строка вида «Chrome 131 на Windows», а
 * нормализованные семейства, с которыми можно сверять другие слои: GPU из
 * WebGL, отпечаток TLS, голоса синтеза речи. Поэтому парсер намеренно грубый,
 * но предсказуемый.
 */

interface BrowserRule {
  name: string;
  /** Первая захваченная группа — версия. */
  pattern: RegExp;
}

// Порядок значим: производные от Chromium браузеры маскируются под Chrome
// и должны проверяться раньше него.
const BROWSER_RULES: BrowserRule[] = [
  { name: 'Edge', pattern: /Edg(?:e|A|iOS)?\/([\d.]+)/ },
  { name: 'Opera', pattern: /(?:OPR|OPiOS)\/([\d.]+)/ },
  { name: 'Opera', pattern: /Opera[/ ]([\d.]+)/ },
  { name: 'Yandex', pattern: /YaBrowser\/([\d.]+)/ },
  { name: 'Vivaldi', pattern: /Vivaldi\/([\d.]+)/ },
  { name: 'Brave', pattern: /Brave\/([\d.]+)/ },
  { name: 'Samsung Internet', pattern: /SamsungBrowser\/([\d.]+)/ },
  { name: 'UC Browser', pattern: /UCBrowser\/([\d.]+)/ },
  { name: 'Firefox', pattern: /(?:Firefox|FxiOS)\/([\d.]+)/ },
  { name: 'Chrome', pattern: /(?:Chrome|CriOS|Chromium)\/([\d.]+)/ },
  { name: 'Safari', pattern: /Version\/([\d.]+).*Safari/ },
  { name: 'Electron', pattern: /Electron\/([\d.]+)/ },
  { name: 'PhantomJS', pattern: /PhantomJS\/([\d.]+)/ },
];

function detectOs(ua: string): { os: string; version: string; family: string } {
  let m: RegExpMatchArray | null;

  if ((m = ua.match(/Windows NT ([\d.]+)/))) {
    const nt = m[1];
    const names: Record<string, string> = {
      '10.0': '10/11',
      '6.3': '8.1',
      '6.2': '8',
      '6.1': '7',
      '6.0': 'Vista',
      '5.1': 'XP',
    };
    return { os: 'Windows', version: names[nt] ?? nt, family: 'windows' };
  }
  if (/Windows/.test(ua)) return { os: 'Windows', version: '', family: 'windows' };

  if ((m = ua.match(/(?:iPhone|iPad|iPod).*?OS ([\d_]+)/))) {
    return { os: 'iOS', version: m[1].replace(/_/g, '.'), family: 'ios' };
  }
  if (/iPhone|iPad|iPod/.test(ua)) return { os: 'iOS', version: '', family: 'ios' };

  if ((m = ua.match(/Android ([\d.]+)/))) {
    return { os: 'Android', version: m[1], family: 'android' };
  }
  if (/Android/.test(ua)) return { os: 'Android', version: '', family: 'android' };

  if (/CrOS/.test(ua)) {
    const v = ua.match(/CrOS \S+ ([\d.]+)/);
    return { os: 'ChromeOS', version: v ? v[1] : '', family: 'linux' };
  }
  if ((m = ua.match(/Mac OS X ([\d_.]+)/))) {
    return { os: 'macOS', version: m[1].replace(/_/g, '.'), family: 'macos' };
  }
  if (/Macintosh|Mac OS X/.test(ua)) return { os: 'macOS', version: '', family: 'macos' };
  if (/(?:Ubuntu|Fedora|Debian|SUSE)/.test(ua)) {
    return { os: 'Linux', version: '', family: 'linux' };
  }
  if (/X11|Linux/.test(ua)) return { os: 'Linux', version: '', family: 'linux' };
  if (/FreeBSD|OpenBSD|NetBSD/.test(ua)) return { os: 'BSD', version: '', family: 'bsd' };

  return { os: 'Unknown', version: '', family: 'other' };
}

function detectEngine(ua: string, browser: string): { engine: string; version: string } {
  let m: RegExpMatchArray | null;

  if (browser === 'Firefox' && !/FxiOS/.test(ua)) {
    m = ua.match(/rv:([\d.]+)/);
    return { engine: 'Gecko', version: m ? m[1] : '' };
  }
  // На iOS любой браузер обязан использовать системный WebKit.
  if (/iPhone|iPad|iPod/.test(ua)) {
    m = ua.match(/AppleWebKit\/([\d.]+)/);
    return { engine: 'WebKit', version: m ? m[1] : '' };
  }
  if (browser === 'Safari') {
    m = ua.match(/AppleWebKit\/([\d.]+)/);
    return { engine: 'WebKit', version: m ? m[1] : '' };
  }
  if ((m = ua.match(/Chrome\/([\d.]+)/))) {
    return { engine: 'Blink', version: m[1] };
  }
  if ((m = ua.match(/Gecko\/(\d+)/))) {
    return { engine: 'Gecko', version: m[1] };
  }
  if ((m = ua.match(/AppleWebKit\/([\d.]+)/))) {
    return { engine: 'WebKit', version: m[1] };
  }
  return { engine: 'Unknown', version: '' };
}

export function parseUserAgent(ua: string | null): UaParsed | null {
  if (!ua) return null;

  let browser = 'Unknown';
  let browserVersion = '';
  for (const rule of BROWSER_RULES) {
    const m = ua.match(rule.pattern);
    if (m) {
      browser = rule.name;
      browserVersion = m[1] ?? '';
      break;
    }
  }

  const { os, version: osVersion, family: platformFamily } = detectOs(ua);
  const { engine, version: engineVersion } = detectEngine(ua, browser);

  const major = Number.parseInt(browserVersion.split('.')[0] ?? '', 10);

  // Версия Chromium берётся отдельно: у Yandex, Opera, Vivaldi и Samsung
  // Internet своя нумерация, и сверять с ней возраст возможностей языка
  // бессмысленно — Yandex 25 работает на Chromium 13x.
  const chromiumMatch = ua.match(/Chrome\/(\d+)/);
  const chromiumMajor = chromiumMatch ? Number.parseInt(chromiumMatch[1] ?? '', 10) : Number.NaN;

  return {
    browser,
    browserVersion,
    browserMajor: Number.isFinite(major) ? major : null,
    chromiumMajor: Number.isFinite(chromiumMajor) ? chromiumMajor : null,
    engine,
    engineVersion,
    os,
    osVersion,
    isMobile: /Mobi|Android|iPhone|iPad|iPod/.test(ua),
    platformFamily,
  };
}

/** Ожидаемый движок JS для семейства браузера — сверяется с реально измеренным. */
export function expectedJsEngine(browser: string, platformFamily: string): string | null {
  if (platformFamily === 'ios') return 'javascriptcore';
  switch (browser) {
    case 'Firefox':
      return 'spidermonkey';
    case 'Safari':
      return 'javascriptcore';
    case 'Chrome':
    case 'Edge':
    case 'Opera':
    case 'Yandex':
    case 'Vivaldi':
    case 'Brave':
    case 'Samsung Internet':
    case 'Electron':
      return 'v8';
    default:
      return null;
  }
}

// Нормализация названий платформ живёт в схеме: она нужна и коллектору,
// и движку правил, а браузерных API не требует.
export { platformFamilyOf, uaDataPlatformFamily } from '@fraudeai/schema';
